import { PropsWithChildren } from 'react';
import Image, { ImageProps } from 'next/image';
import { Tags } from '@/components/Layouts/Intros/ArticleIntro/components/Tags';
import { ITag } from '@/interfaces/articles';
import { Arrow } from '@/images/svg/Arrow';
import Link from 'next/link';
import clsx from 'clsx';

interface IProps extends PropsWithChildren {
  image: ImageProps | null;
  tags: ITag[];
  timing?: string;
  description: string;
  cardType?: string;
  href: string;
  titleChunks?: string[] | null;
  activeTags?: string[] | null;
  onClick?: () => void;
}

export const InsightsCard = ({
  image,
  tags,
  timing,
  description,
  href,
  titleChunks,
  cardType,
  activeTags,
  onClick,
}: IProps) => {
  return (
    <Link
      data-testid="insights-card"
      href={href}
      className="
        block mobile:min-h-[403px]
        mobile:border-[1px] mobile:border-grey-1 group max-w-[590px]
        mobile:bg-white
        mobile:p-[24px]
      "
      onClick={onClick}
    >
      <div
        className="flex flex-col gap-[20px]"
        data-testid="card-container"
      >
        <div
          data-testid="card-image-wrapper"
          className="
            w-full mobile:min-h-[240px] relative overflow-hidden group
            aspect-[343/185] mobile:aspect-[397/240]
           "
        >
          {image ? (
            <Image
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              className={clsx('w-full h-full object-cover', {
                ['duration-300 group-hover:scale-[1.08]']: cardType === 'secondary' || cardType === 'centered',
              })}
              data-testid="card-image"
            />
          ) : (
            <Image
              src="/images/insights/placeholder-blog.webp"
              alt="placeholder"
              width={445}
              height={240}
              className="w-full h-full object-cover"
              data-testid="card-placeholder-image"
            />
          )}

          {cardType === 'secondary' && (
            <div
              data-testid="secondary-badge"
              className="
                bg-[url(/logos/sed.svg)] absolute z-50 top-[14px]
                mobile:top-[24px] left-[30px] md-tablet:left-[40px] w-[240px] h-[23px]
              "
            />
          )}

          {cardType === 'centered' && (
            <div
              data-testid="centered-badge"
              className="
                bg-[url(/logos/sed.svg)] absolute z-50 top-[24px]
                left-1/2 translate-x-[-50%] transform w-[240px] h-[23px]
              "
            />
          )}

          <div
            className={clsx(
              `absolute w-full h-full flex justify-start items-center z-50 top-0 left-0
              [@media(min-width:1440px)]:pl-[40px]
              [@media(min-width:1440px)]:pr-[5px]
              pl-[30px] pr-[5px]
              `,
              {
                ['!items-start !px-[10px] pt-[40px] !justify-center']: cardType === 'centered',
              },
            )}
            data-testid="title-wrapper"
          >
            {titleChunks && (
              <h2
                className={clsx('flex flex-col text-white items-start !font-normal', {
                  ['text-[16.95px] mobile:text-xl [@media(min-width:1440px)]:text-xl pt-[25px]']:
                    cardType === 'secondary' || cardType === 'centered',
                  ['text-xl [@media(min-width:1440px)]:text-[24px]']: cardType === 'primary',
                  ['text-center']: cardType === 'centered',
                })}
                data-testid="card-title"
              >
                {titleChunks.map((chunk, index) => (
                  <span
                    className={clsx('block py-[4px]', {
                      ['leading-[20.34px] mobile:leading-[24px] bg-purple-5 px-[8px]']:
                        cardType === 'secondary' || cardType === 'centered',
                      ['leading-[28.8px] odd:bg-purple-7 even:bg-purple-6 px-base']: cardType === 'primary',
                    })}
                    key={index}
                    data-testid={`title-chunk-${index}`}
                  >
                    {chunk}
                  </span>
                ))}
              </h2>
            )}
          </div>

          {cardType === 'primary' && (
            <div
              className="bg-[url(/backgrounds/polygons-group-purple.svg)] w-[602.34px] h-[684.81px]
               bg-contain absolute top-[-272px] duration-300 group-hover:scale-[1.08] will-change-transform"
              data-testid="primary-background"
            />
          )}
        </div>
        <div
          className="flex flex-col gap-base w-full"
          data-testid="card-content"
        >
          <div
            className="flex justify-between items-center gap-[4px]"
            data-testid="card-tags-and-timing"
          >
            <Tags
              listWrapperClassName="flex gap-[4px]"
              itemClassName="bg-white"
              list={tags}
              activeTags={activeTags}
              data-testid="card-tags"
            />

            <div
              className="flex gap-[4px] items-center self-start shrink-0"
              data-testid="card-timing"
            >
              <div className="text-xs leading-lg font-medium text-black-9 shrink-0">{timing}</div>

              <div
                className="w-[23px] mobile:w-[18px] h-[27px]"
                data-testid="card-arrow"
              >
                <Arrow
                  className="w-full h-full"
                  variant="primary"
                />
              </div>
            </div>
          </div>
          <div
            className="line-clamp-2 text-black-8 leading-[26px] !font-normal !text-base"
            data-testid="card-description"
          >
            {description}
          </div>
        </div>
      </div>
    </Link>
  );
};
